import * as _ from 'lodash';
import { RecruitmentEntityType, RecruitmentPrivacyMode } from '../../../../../shared/types/recruitmentTypes';
import { TAction } from '../actions/types/action';
import { serializeRecruitmentProfile } from '../../../../../shared/frontend/scripts/recruitment/utils';
import { ERROR_MESSAGES } from '../../../../../shared/frontend/scripts/recruitment/constants';
import { LOCATION_CHANGE } from '../../../../../shared/frontend/libs/router/redux-store';
import { namedRoutes } from '../bundles/routes';
import { matchRoutePath } from '../../../../../shared/frontend/libs/router/router-helpers';
import { stripPathLocale } from '../../../../../shared/common/localeShared';

const Character = (id: number, ordinal?: number) => ({
  persona_id: id,
  ordinal,
  mainspec_0_id: undefined,
  mainspec_1_id: undefined,
  mainspec_2_id: undefined,
  mainspec_3_id: undefined,
  offspec_0_id: undefined,
  offspec_1_id: undefined,
  offspec_2_id: undefined,
  offspec_3_id: undefined
});

const baseProfile = {
  privacy_mode: RecruitmentPrivacyMode.Public,
  caption: undefined,
  schedule_timezone: undefined,
  activity_type: undefined,
  tags: [],

  commitment_days_min: 0,
  commitment_days_max: 0,
  commitment_hours_min: 0,
  commitment_hours_max: 0,

  language_0: undefined,
  language_1: undefined,
  language_2: undefined,

  scheduleType: '',
  schedule: [],

  requirements: []
};

const baseCharacterProfile = {
  ...baseProfile,
  entity_type: RecruitmentEntityType.Character,
  characters: []
};

const baseGuildProfile = {
  ...baseProfile,
  entity_type: RecruitmentEntityType.Guild,
  scheduleType: 'custom',
  slots: [
    {
      ordinal: 0,
      priority: 1,
      entries: []
    }
  ]
};

const initialState = {
  loading: false,
  loaded: false,
  error: false,
  errorMessage: '',

  profileId: -1, // profile id of the created/saved profile...todo

  profileType: '',
  id: -1,
  activeStep: '',

  characterProfile: baseCharacterProfile,
  guildProfile: baseGuildProfile,

  // static data:
  tags: [],
  requirements: [],
  userProfile: {},
  saveStatus: {
    loading: false,
    loaded: false,
    error: false
  }
};

const LOAD_PROFILE = 'RECRUIT/CONFIG/LOAD_PROFILE';
const LOAD_PROFILE_FAIL = 'RECRUIT/CONFIG/LOAD_PROFILE_FAIL';
const loadProfile = (profileId: number, activeStep?: string) => ({
  type: LOAD_PROFILE,
  payload: {
    activeStep,
    profileId,
    loading: profileId > -1 ? true : false,
    loaded: profileId === -1 || _.isNil(profileId) ? true : false
  }
});
const loadProfileFail = () => ({
  type: LOAD_PROFILE_FAIL,
  payload: { loading: false, error: true, loaded: true }
});

const SET_EDIT_PROFILE = 'RECRUITMENT/EDIT/SET_PROFILE';
const setEditProfile = (profile: any) => ({
  type: SET_EDIT_PROFILE,
  payload: profile
});
const SELECT_PROFILE_TYPE = 'RECRUITMENT/CREATE/SEL_PROFILE_TYPE';
const selectProfileType = (profileType: string, id: number) => ({
  type: SELECT_PROFILE_TYPE,
  payload: { profileType, id }
});

const CHANGE_STEP = 'RECRUITMENT/CREATE/CHANGE_ACTIVE_STEP';
const changeStep = (nextStep: string) => ({
  type: CHANGE_STEP,
  payload: { nextStep }
});

// this likely won't work for every case, but its a start
const UPDATE_PROFILE_FIELD = 'RECRUITMENT/CREATE/UPDATE_PROFILE_FIELD';
const updateProfileField = (profilePath: string, fieldPath: string, value: any) => ({
  type: UPDATE_PROFILE_FIELD,
  payload: {
    profilePath,
    fieldPath,
    value
  }
});

const ADD_SECONDARY_CHAR = 'RECRUITMENT/CREATE/ADD_SECONDARY_CHAR';
const addSecondaryCharacter = (id: number) => ({
  type: ADD_SECONDARY_CHAR,
  payload: { persona_id: id }
});
const REMOVE_SECONDARY_CHARACTER = 'RECRUITMENT/CREATE/REMOVE_SECONDARY_CHAR';
const removeSecondaryCharacter = (id: number) => ({
  type: REMOVE_SECONDARY_CHARACTER,
  payload: { persona_id: id }
});

// creating/saving profile
const CREATE_PROFILE_REQUEST = 'RECRUITMENT/PROFILE_REQUEST';
const requestProfileCreate = () => ({
  type: CREATE_PROFILE_REQUEST,
  payload: { loading: true }
});
const CREATE_PROFILE_SUCCESS = 'RECRUITMENT/CREATE_SUCCESS';
const createRecruitmentProfileSuccess = (profile: any) => ({
  type: CREATE_PROFILE_SUCCESS,
  payload: {
    ...profile,
    loading: false,
    loaded: true
  }
});
const SAVE_PROFILE = 'RECRUITMENT/CREATE/SAVE_PROFILE';
const SAVE_PROFILE_SUCCESS = 'RECRUITMENT/CREATE/SAVE_PROFILE_SUCCESS';
const SAVE_PROFILE_FAIL = 'RECRUITMENT/CREATE/SAVE_PROFILE_FAIL';
const saveProfile = (saveType?: boolean) => ({
  type: SAVE_PROFILE,
  payload: {
    saveStatus: {
      saveType,
      loading: true
    }
  }
});
const saveProfileSuccess = (profile: any) => ({
  type: SAVE_PROFILE_SUCCESS,
  payload: {
    profile,
    saveStatus: {
      saveType: '',
      loading: false,
      loaded: true,
      error: false
    }
  }
});
const saveProfileFail = () => ({
  type: SAVE_PROFILE_FAIL,
  payload: {
    error: true,
    errorMessage: ERROR_MESSAGES.SAVE_PROFILE_FAIL,
    saveStatus: {
      saveType: '',
      error: true,
      loaded: true,
      loading: false
    }
  }
});

// guild slot updates TODO: make slot/entry subreducer
const ADD_GUILD_SLOT = 'RECRUITMENT/CRATE/ADD_GUILD_SLOT';
const REMOVE_GUILD_SLOT = 'RECRUITMENT/CREATE/REMOVE_GUILD_SLOT';
const DUPLICATE_GUILD_SLOT = 'RECRUITMENT/CREATE/DUPLICATE_GUILD_SLOT';
const ADD_SLOT_ENTRY = 'RECRUITMENT/CREATE/ADD_SLOT_ENTRY';
const DUPLICATE_SLOT_ENTRY = 'RECRUITMENT/CREATE/DUPLICATE_SLOT_ENTRY';
const REMOVE_SLOT_ENTRY = 'RECRUITMENT/CREATE/REMOVE_SLOT_ENTRY';

const addGuildSlot = () => ({ type: ADD_GUILD_SLOT });
const removeGuildSlot = (location: number) => ({ payload: { location }, type: REMOVE_GUILD_SLOT });
const duplicateGuildSlot = (location: number) => ({ payload: { location }, type: DUPLICATE_GUILD_SLOT });
const addSlotEntry = (slotLocation: number) => ({
  type: ADD_SLOT_ENTRY,
  payload: { slotLocation }
});
const duplicateSlotEntry = (slotLocation: number, entryLocation: number) => ({
  type: DUPLICATE_SLOT_ENTRY,
  payload: { slotLocation, entryLocation }
});
const removeSlotEntry = (slotLocation: number, entryLocation: number) => ({
  type: REMOVE_SLOT_ENTRY,
  payload: { slotLocation, entryLocation }
});

const LOAD_STATIC_DATA_SUCCESS = 'RECRUITMENT/CREATE/LOAD_STATIC_DATA_SUCCESS';
const LOAD_STATIC_DATA_FAIL = 'RECRUITMENT/CREATE/LOAD_STATIC_DATA_FAIL';
const loadStaticProfileDataSuccess = (payload: any) => ({ type: LOAD_STATIC_DATA_SUCCESS, payload });
const loadStaticProfileDataFail = () => ({
  type: LOAD_STATIC_DATA_FAIL,
  payload: {
    error: true,
    errorMessage: ERROR_MESSAGES.LOAD_STATIC_DATA_FAIL
  }
});

const CREATE_PROFILE_FAIL = 'RECRUITMENT/CREATE/CREATE_PROFILE_FAIL';
const createProfileFail = (error: { message?: string; error?: string }) => ({
  type: CREATE_PROFILE_FAIL,
  payload: {
    errorMessage: error?.message || error?.error || ERROR_MESSAGES.CREATE_PROFILE_FAIL,
    error: true,
    loading: false,
    loaded: true
  }
});

// allow the error modal to be closed for non-fatal errors
const CLEAR_ERR_STATE = 'RECRUITMENT/CREATE/CLEAR_ERRR_STATE';
const clearErrorState = () => ({
  type: CLEAR_ERR_STATE,
  payload: { error: false, errorMessage: '' }
});

const LOAD_USER_PROFILE_SUCCESS = 'RECRUITMENT/PROFILES/LOAD_USER_PROFILE_SUCCESS';
const LOAD_USER_PROFILE_FAIL = 'RECRUITMENT/PROFILES/LOAD_USER_PROFILE_FAIL';
const loadUserProfileSuccess = (userProfile: any) => ({ type: LOAD_USER_PROFILE_SUCCESS, payload: { userProfile } });
const loadUserProfileFail = () => ({ type: LOAD_USER_PROFILE_FAIL });

function createRecruitmentProfileReducer(
  // state: Partial<TCreateRecruitmentProfileState> = initialState,
  state: any = initialState,
  action: TAction<any>
) {
  switch (action.type) {
    case SAVE_PROFILE:
    case SAVE_PROFILE_SUCCESS:
    case SAVE_PROFILE_FAIL:
    case LOAD_STATIC_DATA_FAIL:
    case CLEAR_ERR_STATE:
    case CREATE_PROFILE_FAIL:
    case CREATE_PROFILE_REQUEST:
    case LOAD_PROFILE_FAIL:
    case LOAD_STATIC_DATA_SUCCESS:
    case LOAD_USER_PROFILE_SUCCESS:
    case LOAD_PROFILE: {
      return { ...state, ...action.payload };
    }
    case SET_EDIT_PROFILE: {
      const profile = action.payload;
      const profileType = profile.profile.entity_type;
      const profileTypeKey = `${profileType}Profile`;
      const profTypeState = state[profileTypeKey];

      let id = 0;
      if (profileType === RecruitmentEntityType.Character) {
        id = _.find(profile.characters, { ordinal: 0 })?.persona_id;
      } else if (profileType === RecruitmentEntityType.Guild) {
        id = profile.guild.guild.id;
      }

      return {
        ...state,
        loading: false,
        loaded: true,
        profileType,
        id,
        [profileTypeKey]: serializeRecruitmentProfile(profTypeState, profile),
        profileId: profile.profile.id
      };
    }
    case CREATE_PROFILE_SUCCESS: {
      const { id } = action.payload;
      return {
        ...state,
        profileId: id,
        loading: false,
        loaded: true
      };
    }
    case ADD_GUILD_SLOT: {
      // todo: handle ordinal for each slot action
      const { guildProfile, profileType } = state;
      const profToUpdate = guildProfile;
      const profileKey = `${profileType}Profile`;

      const slots = [...profToUpdate.slots];
      slots.push({
        priority: 1,
        entries: [],
        ordinal: slots.length
      });

      return {
        ...state,
        [profileKey]: { ...profToUpdate, slots }
      };
    }
    case REMOVE_GUILD_SLOT: {
      const { location } = action.payload;
      const { guildProfile, profileType } = state;
      const profToUpdate = guildProfile;
      const profileKey = `${profileType}Profile`;

      const slots = _.cloneDeep(profToUpdate.slots);

      // Filter out the slot to remove
      const filteredSlots = slots.filter((slot: any) => slot.ordinal !== location);

      // Reindex all remaining slots with sequential ordinals
      const updatedSlots = filteredSlots.map((slot: any, index: number) => ({
        ...slot,
        ordinal: index
      }));

      return {
        ...state,
        [profileKey]: { ...profToUpdate, slots: updatedSlots }
      };
    }
    case DUPLICATE_GUILD_SLOT: {
      const { location } = action.payload;
      const { guildProfile, profileType } = state;
      const profToUpdate = guildProfile;
      const profileKey = `${profileType}Profile`;

      const newProfile = _.cloneDeep(profToUpdate.slots[location]);
      const slots = _.cloneDeep(profToUpdate.slots);
      slots.splice(location, 0, newProfile);

      // reassign all ordinals
      slots.forEach((slot: any, index: number) => (slot.ordinal = index));

      return {
        ...state,
        [profileKey]: { ...profToUpdate, slots }
      };
    }
    case ADD_SLOT_ENTRY: {
      const { slotLocation } = action.payload;
      const { guildProfile, profileType } = state;
      const profToUpdate = guildProfile;
      const profileKey = `${profileType}Profile`;

      // have to clonedeep, or set will modify the initial state
      const updatedSlots = _.cloneDeep(profToUpdate.slots);
      const updatedEntries = updatedSlots[slotLocation].entries;
      _.set(updatedSlots, `[${slotLocation}].entries`, [...updatedEntries, {}]);

      // Reindex all slots to ensure ordinals are sequential after modification
      updatedSlots.forEach((slot: any, index: number) => (slot.ordinal = index));

      return {
        ...state,
        [profileKey]: { ...profToUpdate, slots: updatedSlots }
      };
    }
    case DUPLICATE_SLOT_ENTRY: {
      const { slotLocation, entryLocation } = action.payload;
      const { guildProfile, profileType } = state;
      const profToUpdate = guildProfile;
      const profileKey = `${profileType}Profile`;

      const updatedSlots = _.cloneDeep(profToUpdate.slots);
      const updatedEntries = updatedSlots[slotLocation].entries;
      const duplicatedEntry = _.cloneDeep(updatedEntries[entryLocation]);
      updatedEntries.splice(entryLocation, 0, duplicatedEntry);
      _.set(updatedSlots, `[${slotLocation}].entries`, updatedEntries);

      // Reindex all slots to ensure ordinals are sequential after modification
      updatedSlots.forEach((slot: any, index: number) => (slot.ordinal = index));

      return {
        ...state,
        [profileKey]: { ...profToUpdate, slots: updatedSlots }
      };
    }
    case REMOVE_SLOT_ENTRY: {
      const { slotLocation, entryLocation } = action.payload;
      const { guildProfile, profileType } = state;
      const profToUpdate = guildProfile;
      const profileKey = `${profileType}Profile`;

      const updatedSlots = _.cloneDeep(profToUpdate.slots);
      const updatedEntries = updatedSlots[slotLocation].entries;
      updatedEntries.splice(entryLocation, 1);
      _.set(updatedSlots, `[${slotLocation}].entries`, updatedEntries);

      // Reindex all slots to ensure ordinals are sequential after modification
      updatedSlots.forEach((slot: any, index: number) => (slot.ordinal = index));

      return { ...state, [profileKey]: { ...profToUpdate, slots: updatedSlots } };
    }
    case REMOVE_SECONDARY_CHARACTER: {
      const { persona_id } = action.payload;
      const { characterProfile } = state;
      const indexToRemove = _.findIndex(characterProfile.characters, { persona_id });
      const characters = [...characterProfile.characters];
      characters.splice(indexToRemove, 1);

      return {
        ...state,
        characterProfile: { ...characterProfile, characters }
      };
    }
    case ADD_SECONDARY_CHAR: {
      const { persona_id } = action.payload;
      const { characterProfile } = state;
      const order = characterProfile.characters.length;

      const characters = [...characterProfile.characters, Character(persona_id, order)];

      return {
        ...state,
        characterProfile: { ...characterProfile, characters }
      };
    }
    case SELECT_PROFILE_TYPE: {
      const { profileType, id } = action.payload;
      const { characterProfile, guildProfile } = state;
      let updatedProfileType = {};

      if (profileType === RecruitmentEntityType.Character) {
        updatedProfileType = {
          characterProfile: {
            ...characterProfile,
            characters: [Character(id, 0)]
          }
        };
      } else if (profileType === RecruitmentEntityType.Guild) {
        updatedProfileType = {
          guildProfile: {
            ...guildProfile
          }
        };
      }

      return {
        ...state,
        ..._.omit(action.payload, 'type'),
        ...updatedProfileType
      };
    }
    case CHANGE_STEP: {
      const { nextStep } = action.payload;

      if (nextStep === '') {
        return initialState;
      }

      return {
        ...state,
        activeStep: nextStep
      };
    }
    case UPDATE_PROFILE_FIELD: {
      const { profilePath, fieldPath, value } = action.payload;

      const updatedProfileValues = _.cloneDeep({ ...state[profilePath] });
      _.set(updatedProfileValues, fieldPath, value);

      return {
        ...state,
        [profilePath]: { ...updatedProfileValues }
      };
    }
    case LOCATION_CHANGE: {
      const path = stripPathLocale(action.payload.location.pathname);

      const shouldReset =
        path !== namedRoutes.RecruitmentProfileCreate &&
        !matchRoutePath({ path: namedRoutes.RecruitmentProfileConfig, end: false }, path);

      if (shouldReset) {
        return initialState;
      }

      return state;
    }
    default:
      return state;
  }
}

export const actions = {
  loadProfile,
  loadProfileFail,
  setEditProfile,

  selectProfileType,
  changeStep,
  updateProfileField,
  addSecondaryCharacter,
  removeSecondaryCharacter,
  requestProfileCreate,
  createRecruitmentProfileSuccess,
  saveProfile,
  saveProfileSuccess,
  saveProfileFail,
  loadStaticProfileDataSuccess,
  loadStaticProfileDataFail,
  clearErrorState,

  addGuildSlot,
  removeGuildSlot,
  duplicateGuildSlot,
  addSlotEntry,
  removeSlotEntry,
  duplicateSlotEntry,

  createProfileFail,
  loadUserProfileSuccess,
  loadUserProfileFail
};
export const actionTypes = {
  SAVE_PROFILE,
  CREATE_PROFILE_SUCCESS,
  UPDATE_PROFILE_FIELD,
  LOAD_PROFILE,
  CHANGE_STEP,
  SAVE_PROFILE_SUCCESS
};

export default createRecruitmentProfileReducer;
